/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
export const pluginsContent = `

## Summary

Dexter's plugin architecture provides a modular, extensible framework for adding new capabilities to the platform. Plugins are self-contained packages that extend Dexter's functionality without modifying the core system, allowing for flexible deployment and independent updates.

Currently, Dexter ships with two core language support plugins:
- **Protobuf Support** - Protocol Buffer parsing and transformation
- **XML Support** - XML format parsing and transformation

Additional language support plugins are under development, including C++, Java, and Python parsers. The plugin system enables Dexter to continuously expand its language and format support while maintaining a stable core platform.

---

## What Are Plugins?

Plugins are modular extensions that add specific capabilities to Dexter. Think of them as specialized tools that snap into the platform to provide new features.

### Plugin Characteristics

**Modularity:**
- Self-contained functionality
- Can be enabled or disabled independently
- Don't interfere with other plugins
- Each has a specific purpose

**Extensibility:**
- Add new features without modifying core Dexter
- Extend existing capabilities
- Integrate seamlessly with Transform Engine
- Support new data formats and languages

**Independent Updates:**
- Plugins can be updated separately from Dexter core
- Version compatibility is maintained
- Bug fixes don't require full system updates
- New plugin versions can be released anytime

**Portability:**
- Plugins are packaged as discrete units
- Can be distributed independently
- Easy installation and configuration
- Consistent interface with Dexter core

---

## Current Plugin Support

Dexter currently includes two production-ready language support plugins.

### Protobuf Plugin

The Protobuf plugin provides comprehensive Protocol Buffer support.

**Capabilities:**
- **Parsing:** Read and parse .proto schema files
- **Data Extraction:** Extract message definitions and field structures into graphical units
- **Type Mapping:** Convert Protobuf types to XML equivalents
- **Transformation:** Full-file and granular transformations
- **Validation:** Schema validation and type checking (future)

**Supported Operations:**
- Protobuf → XML (full-file)
- Protobuf → XML (granular field mapping)

### XML Plugin

The XML plugin provides comprehensive XML support.

**Capabilities:**
- **Parsing:** Read and parse .xml files
- **Data Extraction:** Extract structures into graphical units
- **Type Mapping:** Convert XML files to Protobuf equivalents
- **Transformation:** Full-file and granular transformations
- **Validation:** Schema validation and type checking (future)

**Supported Operations:**
- XML → Protobuf (full-file)
- XML → Protobuf (granular field mapping)

---

## Plugin Architecture

Dexter's plugin system is built on a clean, extensible architecture.

### How Plugins Work

**Plugin Components:**
1. **Parser** - Reads and interprets file formats
2. **Schema Handler** - Manages data structure definitions
3. **Transformer** - Converts data between formats
4. **Validator** - Ensures data integrity and correctness
5. **Visualizer** - Displays data in Transform Engine

**Integration Points:**
- **File Upload** - Plugins detect and parse supported formats
- **Input Nodes** - Plugins provide data structure visualization
- **Transform Nodes** - Plugins enable transformation operations
- **Output Nodes** - Plugins format output data

---

## Future Plugin Development

Dexter's plugin ecosystem will continue to expand with new language support and capabilities.

### Planned Language Support Plugins

**C++ Language Plugin** (Future Development)
- Parse C++ header files
- Extract classes, structs, enums
- Handle templates and inheritance
- Support modern C++ standards (C++11/14/17/20)
- Type system mapping to XML
- Generate transformation schemas

**Java Language Plugin** (Future Development)
- Parse Java source files
- Extract classes, interfaces, annotations
- Handle generics and inheritance
- Support Java 8+ features
- Type system mapping

**Python Language Plugin** (Future Development)
- Parse Python modules
- Extract classes and type hints
- Support dataclasses and NamedTuples
- Handle dynamic typing
- Type hint to schema conversion

---

## Benefits of Plugin Architecture

### For Users

**Flexibility:**
- Install only the plugins you need
- Avoid clutter from unused features
- Customize Dexter for your specific use cases
- Add new capabilities as requirements evolve

**Performance:**
- Faster startup (fewer plugins loaded)
- Reduced memory footprint
- Better resource utilization
- Optimized for your workflows

**Stay Current:**
- Update individual plugins without full system upgrade
- Access latest features immediately
- Independent plugin release cycles
- No waiting for monolithic updates


---

## Using Plugins

### Current Experience

**Automatic Plugin Integration:**
Dexter's current plugins (Protobuf and XML) are built-in and work automatically:

1. Upload a file
2. Dexter detects the format using plugins
3. Appropriate parser activates
4. Data becomes available in Transform Engine
5. Compatible transforms appear automatically

**No Configuration Required:**
- Plugins work out of the box
- No installation or activation needed
- Seamless integration with workflows
- Transparent operation

### Future Plugin Management

As the plugin ecosystem expands, Dexter will introduce plugin management features:

**Plugin Catalog:**
- Browse available plugins
- View plugin descriptions and capabilities
- Check compatibility and requirements

**Installation:**
- One-click plugin installation
- Automatic dependency resolution
- Version compatibility checks

**Configuration:**
- Per-plugin settings and preferences
- Enable/disable plugins as needed
- Configure plugin behavior
- Manage plugin updates

`;
